import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import html2pdf from "html2pdf.js";
import { formatBRDate } from "../utils/date";
import { 
  Receipt, 
  X, 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  Coins, 
  Download, 
  Sparkles, 
  QrCode,
  Calendar,
  Building2,
  Check,
  Pencil,
  Trash2,
  Save,
  Printer,
  FileDown,
  Filter,
  Mail,
  Layers
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MemberStatementModal = ({
  isOpen,
  onClose,
  statementData,
  treasurySettings,
  costCenters,
  onPayFee,
  onMarkLinkedPaid,
  onReload,
  currentUserRole,
  canManage = true, // Tesoureiro/Master. Se false → modo consulta (sem checkbox/pagar/editar/excluir)
}) => {
  const [selectedFeeToPay, setSelectedFeeToPay] = useState(null);
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState("PIX");
  const [costCenterId, setCostCenterId] = useState("cc_01");
  const [notes, setNotes] = useState("");
  const [amountReceived, setAmountReceived] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingFeeId, setEditingFeeId] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");
  const [selectedFeeIds, setSelectedFeeIds] = useState([]);
  const [bulkPayOpen, setBulkPayOpen] = useState(false);
  const [bulkAmountReceived, setBulkAmountReceived] = useState("");
  const [singlePreview, setSinglePreview] = useState(null);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [payingEventChargeId, setPayingEventChargeId] = useState(null);
  const [payingLinkedTransactionId, setPayingLinkedTransactionId] = useState(null);
  const [evPayForm, setEvPayForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: "PIX",
    cost_center_id: "cc_01",
    amount_received: "",
    notes: "",
  });

  const rateio = statementData?.rateio || { enabled: false, rule_count: 0 };
  const hasRateio = rateio.enabled;

  const handleLinkedTransactionPaid = async (transaction) => {
    if (!onMarkLinkedPaid) return;
    setPayingLinkedTransactionId(transaction.id);
    try {
      const success = await onMarkLinkedPaid(transaction);
      if (success && onReload) await onReload();
    } finally {
      setPayingLinkedTransactionId(null);
    }
  };

  const fees = useMemo(() => statementData?.fees || [], [statementData?.fees]);
  const gestaoStart = fees?.[0]?.due_date || "";
  const gestaoEnd = fees?.[fees.length - 1]?.due_date || "";
  const effStart = filterStart || gestaoStart;
  const effEnd = filterEnd || gestaoEnd;
  const todayISO = new Date().toISOString().slice(0, 10);

  // Fees dentro do período filtrado
  const filteredFees = useMemo(() => {
    return (fees || []).filter((f) => {
      const d = f.due_date || "";
      if (!d) return true;
      if (effStart && d < effStart) return false;
      if (effEnd && d > effEnd) return false;
      return true;
    });
  }, [fees, effStart, effEnd]);

  const {
    member = {}, total_paid = 0, total_open = 0, total_overdue = 0, total_future = 0,
    overdue_count = 0, credit_balance = 0, event_charges = [], event_total_paid = 0,
    event_total_open = 0, default_bulk_fee_ids = [], policy = {}, linked_transactions = [],
    linked_debit_open = 0, linked_credit_open = 0, linked_net_open = 0,
  } = statementData || {};
  const dueDay = policy.due_day || treasurySettings?.due_day || 10;
  const discountVal = policy.prompt_discount_value ?? treasurySettings?.prompt_discount_value ?? 0;

  // Cobranças de eventos: sempre mostra TODAS (não filtra por período)
  // — muitas Lojas têm poucos eventos por Irmão e o filtro escondia cobranças pendentes
  const filteredEventCharges = event_charges || [];

  const applyPreset = (preset) => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (preset === "gestao") { setFilterStart(gestaoStart); setFilterEnd(gestaoEnd); }
    else if (preset === "3m") {
      const past = new Date(today); past.setMonth(past.getMonth() - 3);
      setFilterStart(iso(past)); setFilterEnd(iso(today));
    } else if (preset === "ano") {
      setFilterStart(`${today.getFullYear()}-01-01`);
      setFilterEnd(`${today.getFullYear()}-12-31`);
    } else if (preset === "clear") {
      setFilterStart(""); setFilterEnd("");
    }
  };

  const pastFees = useMemo(() => filteredFees.filter((f) => !f.is_future), [filteredFees]);
  const futureFees = useMemo(() => filteredFees.filter((f) => f.is_future), [filteredFees]);
  const selectableFees = useMemo(() => filteredFees.filter((f) => f.status !== "pago"), [filteredFees]);
  const selectedFees = useMemo(
    () => selectableFees.filter((f) => selectedFeeIds.includes(f.id)),
    [selectableFees, selectedFeeIds],
  );

  useEffect(() => {
    if (!isOpen || !statementData?.member?.id) return;
    setSelectedFeeIds(default_bulk_fee_ids);
    setBulkPayOpen(false);
    setBulkAmountReceived("");
  }, [isOpen, statementData?.member?.id, default_bulk_fee_ids]);

  useEffect(() => {
    if (!isOpen || !selectedFeeToPay?.month) {
      setSinglePreview(null);
      return;
    }
    const timer = setTimeout(() => {
      axios.post(`${API}/treasury/payment-preview`, {
        member_id: member.id,
        months: [selectedFeeToPay.month],
        payment_date: payDate,
        amount_received: amountReceived === "" ? null : Number(String(amountReceived).replace(",", ".")),
      }).then((response) => setSinglePreview(response.data)).catch(() => setSinglePreview(null));
    }, 180);
    return () => clearTimeout(timer);
  }, [isOpen, selectedFeeToPay?.month, member.id, payDate, amountReceived]);

  useEffect(() => {
    if (!isOpen || selectedFees.length === 0) {
      setBulkPreview(null);
      return;
    }
    const timer = setTimeout(() => {
      axios.post(`${API}/treasury/payment-preview`, {
        member_id: member.id,
        months: selectedFees.map((fee) => fee.month),
        payment_date: payDate,
        amount_received: bulkAmountReceived === "" ? null : Number(String(bulkAmountReceived).replace(",", ".")),
      }).then((response) => setBulkPreview(response.data)).catch(() => setBulkPreview(null));
    }, 180);
    return () => clearTimeout(timer);
  }, [isOpen, member.id, payDate, bulkAmountReceived, selectedFees]);

  if (!isOpen || !statementData) return null;

  const printStatementPDF = async () => {
    const src = document.getElementById("stmt-print-root");
    if (!src) {
      toast.error("Não foi possível localizar o extrato para exportar.");
      return;
    }
    const safeName = (member?.name || "extrato").replace(/[^\w\s-]/g, "").replace(/\s+/g, "_");
    const periodTag = `${effStart || "inicio"}_a_${effEnd || "fim"}`;
    const filename = `Extrato_${safeName}_${periodTag}.pdf`;

    // Cria wrapper invisível ao usuário (overflow:hidden + tamanho zero) mas com o clone renderizado dentro
    const wrapper = document.createElement("div");
    wrapper.id = "stmt-print-wrapper";
    wrapper.setAttribute("style",
      "position: fixed; top: 0; left: 0; width: 0; height: 0; overflow: hidden; opacity: 0; pointer-events: none;"
    );

    const clone = src.cloneNode(true);
    // Mantém o mesmo id para que as regras CSS internas (#stmt-print-root ...) continuem funcionando.
    // Remove o original temporariamente do documento para não haver duplicidade de id.
    const originalParent = src.parentNode;
    const originalNextSibling = src.nextSibling;
    if (originalParent) originalParent.removeChild(src);

    clone.setAttribute("style",
      "position: static; width: 794px; min-height: 200px; " +
      "background: #ffffff; color: #111; padding: 24px; " +
      "font-family: Georgia, 'Times New Roman', serif; " +
      "opacity: 1; visibility: visible; " +
      "box-shadow: none;"
    );
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    toast.message("Gerando PDF...");
    try {
      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename,
          image: { type: "jpeg", quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false, width: 794, windowWidth: 794 },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"] },
        })
        .from(clone)
        .save();
      toast.success("PDF baixado! Anexe no WhatsApp e envie.");
    } catch (err) {
      toast.error("Falha ao gerar PDF: " + (err?.message || "erro desconhecido"));
    } finally {
      if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
      // Restaura o original ao DOM
      if (originalParent) {
        if (originalNextSibling) originalParent.insertBefore(src, originalNextSibling);
        else originalParent.appendChild(src);
      }
    }
  };

  const singleItem = singlePreview?.fees?.[0] || selectedFeeToPay || {};
  const singleSummary = singlePreview?.summary || {};
  const isDatePrompt = Number(singleItem.prompt_discount_amount || 0) > 0;
  const fullAmountForFee = Number(singleItem.amount_due || 0);
  const prevPartial = Number(selectedFeeToPay?.partial_paid_amount || 0);
  const remainingForFee = Number(singleSummary.total_required ?? selectedFeeToPay?.amount_open ?? 0);
  const parsedAmountReceived = Number(singleSummary.amount_received ?? 0);
  const isPartialPayment = Boolean(singleSummary.is_partial);
  const overpay = Number(singleSummary.overpay || 0);

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(val || 0);
  };

  const handleConfirmPayment = async (e) => {
    e.preventDefault();
    if (!selectedFeeToPay) return;

    setIsSubmitting(true);
    try {
      await onPayFee({
        member_id: member.id,
        month: selectedFeeToPay.month,
        payment_date: payDate,
        payment_method: paymentMethod,
        cost_center_id: costCenterId,
        notes: notes,
        amount_received: amountReceived === "" ? null : parsedAmountReceived,
      });
      setSelectedFeeToPay(null);
      setAmountReceived("");
    } catch (err) {
      toast.error("Erro ao registrar pagamento.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openPayFor = (fee) => {
    setSelectedFeeToPay(fee);
    setAmountReceived(Number(fee.amount_open || 0).toFixed(2));
  };

  const handleSaveAmount = async (fee) => {
    const parsed = parseFloat(String(editAmount).replace(",", "."));
    if (!parsed || parsed <= 0) {
      toast.error("Informe um valor maior que zero.");
      return;
    }
    try {
      await axios.patch(`${API}/treasury/fees/${fee.id}/amount`, { original_amount: parsed });
      toast.success("Valor atualizado.");
      setEditingFeeId(null);
      onReload && onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível atualizar.");
    }
  };

  const handleReverseOrDelete = async (fee) => {
    const isPaid = fee.status === "pago";
    const confirmMsg = isPaid
      ? `Estornar o pagamento de ${fee.month}? A receita será removida do caixa da Loja.`
      : `Remover o lançamento de ${fee.month}?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.delete(`${API}/treasury/fees/${fee.id}`);
      toast.success(isPaid ? "Pagamento estornado." : "Lançamento removido.");
      onReload && onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível remover.");
    }
  };

  const bulkSummary = bulkPreview?.summary || {};
  const bulkTotalRequired = Number(bulkSummary.total_required || 0);
  const bulkParsedReceived = Number(bulkSummary.amount_received || 0);
  const bulkIsPartial = Boolean(bulkSummary.is_partial);
  const bulkOverpay = Number(bulkSummary.overpay || 0);

  const toggleSelectFee = (feeId) => {
    setSelectedFeeIds((prev) => prev.includes(feeId) ? prev.filter((x) => x !== feeId) : [...prev, feeId]);
  };
  const toggleSelectAllOpen = () => {
    const openIds = selectableFees.filter((fee) => fee.is_overdue).map((f) => f.id);
    if (openIds.every((id) => selectedFeeIds.includes(id))) {
      setSelectedFeeIds([]);
    } else {
      setSelectedFeeIds(openIds);
    }
  };
  const openBulkPay = () => {
    setBulkAmountReceived(bulkTotalRequired > 0 ? bulkTotalRequired.toFixed(2) : "");
    setBulkPayOpen(true);
  };
  const handleConfirmBulkPay = async (e) => {
    e.preventDefault();
    if (selectedFees.length === 0) return;
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/treasury/pay-fees-bulk`, {
        member_id: member.id,
        months: selectedFees.map((f) => f.month),
        payment_date: payDate,
        payment_method: paymentMethod,
        cost_center_id: costCenterId,
        notes: notes,
        amount_received: bulkAmountReceived === "" ? null : bulkParsedReceived,
      });
      toast.success(res.data.message);
      setBulkPayOpen(false);
      setSelectedFeeIds([]);
      setBulkAmountReceived("");
      onReload && onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao processar pagamento em lote.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openPayEventCharge = (c) => {
    setPayingEventChargeId(c.id);
    setEvPayForm({
      payment_date: new Date().toISOString().slice(0, 10),
      payment_method: "PIX",
      cost_center_id: c.cost_center_id || "cc_01",
      amount_received: (c.remaining_amount != null ? c.remaining_amount : (c.total_amount || 0) - (c.partial_paid_amount || 0)).toFixed(2),
      notes: "",
    });
  };

  const submitPayEventCharge = async (e, c) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/treasury/event-charges/${c.id}/pay`, {
        ...evPayForm,
        amount_received: parseFloat(evPayForm.amount_received) || 0,
      });
      const msg = res.data.is_full
        ? `Cobrança "${c.event_name}" quitada!${res.data.overpay > 0 ? ` Excedente R$ ${res.data.overpay.toFixed(2)} → crédito.` : ""}`
        : `Pagamento PARCIAL. Falta R$ ${res.data.remaining_amount.toFixed(2)}`;
      toast.success(msg);
      setPayingEventChargeId(null);
      onReload && onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao registrar pagamento.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendStatementEmail = async () => {
    const defaultTo = member.email || "";
    const to = window.prompt("Enviar extrato para qual e-mail?", defaultTo);
    if (!to) return;
    setEmailBusy(true);
    try {
      const res = await axios.post(`${API}/members/${member.id}/statement/email`, {
        start: effStart || null,
        end: effEnd || null,
        to,
      });
      toast.success(`Extrato enviado para ${res.data.to}`);
    } catch (err) {
      const status = err.response?.status;
      if (status === 503) {
        toast.error("Envio de e-mail ainda não está ativo. Configure a chave da Resend primeiro.");
      } else {
        toast.error(err.response?.data?.detail || "Falha ao enviar e-mail.");
      }
    } finally {
      setEmailBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/85 backdrop-blur-md" data-print-scope>
      {/* Print CSS: esconde tudo, mostra só #stmt-print-root */}
      <style>{`
        @media print {
          @page { size: A4; margin: 14mm; }
          html, body { background: #ffffff !important; color: #000 !important; }
          body * { visibility: hidden !important; }
          #stmt-print-root, #stmt-print-root * { visibility: visible !important; }
          #stmt-print-root { position: absolute !important; left: 0 !important; top: 0 !important; right: auto !important; width: 100% !important; max-width: 100% !important; padding: 0 !important; margin: 0 !important; background: #ffffff !important; color: #000 !important; box-shadow: none !important; }
          .stmt-no-print { display: none !important; }
        }
      `}</style>
      <div 
        data-testid="modal-member-statement"
        className="w-full max-w-6xl bg-[#131C2E] border border-[#D4AF37]/40 rounded-xl sm:rounded-2xl shadow-[0_0_60px_rgba(212,175,55,0.3)] overflow-hidden flex flex-col max-h-[96vh] sm:max-h-[92vh] stmt-no-print"
      >
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-3 sm:px-6 py-3 sm:py-4 border-b border-slate-800 bg-[#080C14]">
          <div className="flex items-center space-x-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center text-[#D4AF37] border border-[#D4AF37]/30">
              <Receipt className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 className="font-serif font-bold text-sm sm:text-base text-white break-words">
                Extrato Financeiro Individual — {member.name}
              </h3>
              <p className="text-xs text-slate-400 font-mono">
                CIM: {member.cim} • {member.degree} • {member.office}
              </p>
              {(() => {
                const first = fees?.[0]?.month;
                const last = fees?.[fees.length - 1]?.month;
                if (!first || !last) return null;
                return (
                  <p className="text-[10px] text-[#D4AF37] mt-1 font-semibold uppercase tracking-widest">
                    Gestão: {first} → {last}
                  </p>
                );
              })()}
            </div>
          </div>
          <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
            <button
              data-testid="btn-print-statement-pdf"
              onClick={printStatementPDF}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/25 text-xs font-semibold"
              title="Baixar/Imprimir Extrato em PDF"
            >
              <FileDown className="w-3.5 h-3.5" />
              PDF
            </button>
            <button
              data-testid="btn-email-statement"
              onClick={handleSendStatementEmail}
              disabled={emailBusy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500/15 border border-sky-500/40 text-sky-300 hover:bg-sky-500/25 text-xs font-semibold disabled:opacity-40"
              title="Enviar Extrato por E-mail"
            >
              <Mail className="w-3.5 h-3.5" />
              {emailBusy ? "Enviando..." : "E-mail"}
            </button>
            <button
              data-testid="btn-close-statement-modal"
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filtro de Período */}
        <div className="px-3 sm:px-6 py-3 border-b border-slate-800 bg-[#0B111E] flex flex-wrap items-center gap-2 text-xs">
          <span className="text-slate-400 flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-[#D4AF37]" />
            Período:
          </span>
          <input
            data-testid="input-stmt-filter-start"
            type="date"
            value={filterStart}
            onChange={(e) => setFilterStart(e.target.value)}
            className="bg-[#131C2E] text-white p-1.5 rounded-md border border-slate-700 text-xs"
          />
          <span className="text-slate-500">→</span>
          <input
            data-testid="input-stmt-filter-end"
            type="date"
            value={filterEnd}
            onChange={(e) => setFilterEnd(e.target.value)}
            className="bg-[#131C2E] text-white p-1.5 rounded-md border border-slate-700 text-xs"
          />
          <div className="flex items-center gap-1 ml-2">
            <button data-testid="btn-preset-gestao" onClick={() => applyPreset("gestao")} className="px-2 py-1 rounded-md bg-slate-800 text-slate-300 hover:text-white text-[10px] font-semibold uppercase">Gestão</button>
            <button data-testid="btn-preset-3m" onClick={() => applyPreset("3m")} className="px-2 py-1 rounded-md bg-slate-800 text-slate-300 hover:text-white text-[10px] font-semibold uppercase">3 meses</button>
            <button data-testid="btn-preset-ano" onClick={() => applyPreset("ano")} className="px-2 py-1 rounded-md bg-slate-800 text-slate-300 hover:text-white text-[10px] font-semibold uppercase">Ano</button>
            <button data-testid="btn-preset-clear" onClick={() => applyPreset("clear")} className="px-2 py-1 rounded-md bg-slate-800 text-rose-300 hover:text-white text-[10px] font-semibold uppercase">Limpar</button>
          </div>
          <span className="w-full sm:w-auto sm:ml-auto text-[10px] text-slate-500 font-mono" data-testid="stmt-filter-count">
            {filteredFees.length} lançamento(s) • {pastFees.length} passado(s) • {futureFees.length} futuro(s)
          </span>
        </div>

        {/* Content Body */}
        <div className="p-3 sm:p-6 overflow-y-auto overflow-x-hidden space-y-6 text-xs text-slate-300">
          
          {/* Prompt Discount Rules Banner */}
          <div className="p-4 rounded-xl bg-gradient-to-r from-[#D4AF37]/15 via-[#131C2E] to-[#080C14] border border-[#D4AF37]/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="space-y-1">
              <span className="text-xs font-bold text-[#D4AF37] uppercase tracking-wider flex items-center space-x-1">
                <Sparkles className="w-3.5 h-3.5" />
                <span>Regra de Pontualidade da Tesouraria</span>
              </span>
              <p className="text-xs text-slate-300">
                Vencimento todo <strong>dia {dueDay}</strong>. Pagando até o dia {dueDay}, ganhe{" "}
                <span className="text-emerald-400 font-bold font-mono">
                  {formatCurrency(discountVal)} de desconto
                </span>{" "}
                . Os valores líquidos são calculados automaticamente pela Tesouraria.
              </p>
            </div>

            <div className="flex items-center space-x-4 bg-[#080C14] p-2.5 rounded-lg border border-slate-800 text-right shrink-0">
              <div>
                <span className="text-[10px] text-slate-400 block">Total Quitado:</span>
                <span className="font-mono font-bold text-emerald-400 text-sm" data-testid="stmt-total-paid">{formatCurrency(total_paid)}</span>
              </div>
              <div className="border-l border-slate-800 pl-3">
                <span className="text-[10px] text-slate-400 block" title="Somente vencidos até hoje. Meses futuros não contam.">A Quitar (vencido):</span>
                <span className="font-mono font-bold text-[#D4AF37] text-sm" data-testid="stmt-total-open">{formatCurrency(total_overdue)}</span>
              </div>
              {total_future > 0 && (
                <div className="border-l border-slate-800 pl-3">
                  <span className="text-[10px] text-slate-500 block">A Vencer:</span>
                  <span className="font-mono font-bold text-slate-400 text-sm" data-testid="stmt-total-future">{formatCurrency(total_future)}</span>
                </div>
              )}
              {credit_balance > 0 && (
                <div className="border-l border-slate-800 pl-3">
                  <span className="text-[10px] text-purple-300 block">Crédito:</span>
                  <span className="font-mono font-bold text-purple-300 text-sm" data-testid="stmt-credit-balance">{formatCurrency(credit_balance)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Payment Form Modal Inline if triggered */}
          {canManage && selectedFeeToPay && (
            <form onSubmit={handleConfirmPayment} className="p-4 rounded-xl bg-[#080C14] border border-[#D4AF37] space-y-3">
              <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                <span className="font-serif font-bold text-sm text-white">
                  Quitar Capitação: {selectedFeeToPay.month}
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedFeeToPay(null)}
                  className="text-xs text-slate-400 hover:text-white"
                >
                  Cancelar
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Data do Pagamento</label>
                  <input
                    data-testid="input-pay-fee-date"
                    type="date"
                    required
                    value={payDate}
                    onChange={(e) => setPayDate(e.target.value)}
                    className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700 text-xs"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 mb-1">
                    Valor Recebido
                    <span className="text-[10px] text-slate-500 ml-1">(padrão: restante)</span>
                  </label>
                  <input
                    data-testid="input-pay-fee-amount-received"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={remainingForFee.toFixed(2)}
                    value={amountReceived}
                    onChange={(e) => setAmountReceived(e.target.value)}
                    className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-[#D4AF37]/40 text-xs font-mono"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 mb-1">Forma de Pagamento</label>
                  <select
                    data-testid="select-pay-fee-method"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700 text-xs"
                  >
                    <option value="PIX">PIX (Chave da Loja)</option>
                    <option value="Boleto">Boleto Bancário</option>
                    <option value="Dinheiro">Dinheiro em Espécie</option>
                    <option value="Transferência TED">Transferência TED</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-400 mb-1">Centro de Custo</label>
                  {hasRateio ? (
                    <div
                      data-testid="single-pay-rateio-locked"
                      className="w-full bg-[#080C14] text-[10px] text-slate-400 p-2 rounded-lg border border-[#D4AF37]/30 italic"
                      title="Rateio Automático da Capitação está ativo — o valor será dividido entre os CCs configurados."
                    >
                      🔀 Rateio Automático ativo — dividido entre {rateio.rule_count} CC{rateio.rule_count > 1 ? "s" : ""}
                    </div>
                  ) : (
                    <select
                      value={costCenterId}
                      onChange={(e) => setCostCenterId(e.target.value)}
                      className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700 text-xs"
                    >
                      {costCenters.map(cc => (
                        <option key={cc.id} value={cc.id}>
                          {cc.code} - {cc.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* Preview do Rateio automático — pagamento avulso */}
              {hasRateio && (() => {
                const allocs = singlePreview?.allocations || [];
                if (allocs.length === 0) return null;
                return (
                  <div className="p-3 rounded-lg border border-[#D4AF37]/30 bg-[#080C14]" data-testid="single-pay-rateio-preview">
                    <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-2 flex items-center gap-1.5">
                      <Layers className="w-3 h-3" />
                      Divisão Automática (Rateio) — valor recebido de {formatCurrency(parsedAmountReceived)}
                    </div>
                    <div className="space-y-1">
                      {allocs.map((a) => (
                        <div key={a.cost_center_id} className={`flex items-center justify-between text-[11px] ${a.is_multa ? "border-t border-rose-500/30 pt-1 mt-1" : ""}`}>
                          <span className={a.is_multa ? "text-rose-300 truncate flex items-center gap-1" : "text-slate-300 truncate"}>
                            {a.is_multa && <span title="Multa por atraso — CC imutável">🔒</span>}
                            {a.cost_center_name}
                            {a.is_multa && <span className="text-[9px] italic text-rose-400/70">(multa por atraso)</span>}
                          </span>
                          <span className={`font-mono font-bold ${a.is_multa ? "text-rose-300" : "text-emerald-300"}`}>{formatCurrency(a.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <div className="space-y-2">
                <div className={`p-2.5 rounded-lg border text-xs flex items-center justify-between ${
                  isDatePrompt 
                    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                    : "bg-amber-500/10 border-amber-500/30 text-amber-300"
                }`} data-testid="pay-discount-banner">
                  <span>
                    {isDatePrompt 
                      ? `✓ Pagamento até o vencimento (${selectedFeeToPay?.due_date?.split("-").reverse().join("/") || "—"}): desconto de ${formatCurrency(singleItem.prompt_discount_amount)} aplicado.`
                      : `⚠ Pagamento posterior ao vencimento (${selectedFeeToPay?.due_date?.split("-").reverse().join("/") || "—"}): valor integral sem desconto.`}
                  </span>
                  <span className="font-mono font-bold text-sm text-white">
                    Devido: {formatCurrency(fullAmountForFee)}
                  </span>
                </div>

                {prevPartial > 0 && (
                  <div className="p-2 rounded-lg border border-sky-500/30 bg-sky-500/10 text-sky-200 text-[11px] flex items-center justify-between" data-testid="pay-previous-partial-banner">
                    <span>💰 Já pago parcialmente: {formatCurrency(prevPartial)}</span>
                    <span className="font-mono font-bold">Restante: {formatCurrency(remainingForFee)}</span>
                  </div>
                )}

                {isPartialPayment && parsedAmountReceived > 0 && (
                  <div className="p-2 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-200 text-[11px] flex items-center justify-between" data-testid="pay-partial-banner">
                    <span>⚠ Pagamento parcial. Ficará devendo {formatCurrency(remainingForFee - parsedAmountReceived)} desta mensalidade.</span>
                    <span className="font-mono font-bold">Recebendo: {formatCurrency(parsedAmountReceived)}</span>
                  </div>
                )}

                {overpay > 0 && (
                  <div className="p-2 rounded-lg border border-purple-500/40 bg-purple-500/10 text-purple-200 text-[11px] flex items-center justify-between" data-testid="pay-overpay-banner">
                    <span>✨ Excedente de {formatCurrency(overpay)} será adicionado ao crédito do Irmão para a próxima mensalidade.</span>
                    <span className="font-mono font-bold">Recebendo: {formatCurrency(parsedAmountReceived)}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-2 pt-1">
                <button
                  type="button"
                  onClick={() => setSelectedFeeToPay(null)}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 hover:text-white text-xs"
                >
                  Voltar
                </button>
                <button
                  data-testid="btn-confirm-pay-fee"
                  type="submit"
                  disabled={isSubmitting || parsedAmountReceived <= 0}
                  className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-4 py-1.5 rounded-lg text-xs uppercase tracking-wider disabled:opacity-40"
                >
                  {isSubmitting 
                    ? "Processando..." 
                    : isPartialPayment 
                      ? `Registrar Parcial (${formatCurrency(parsedAmountReceived)})`
                      : overpay > 0
                        ? `Quitar + ${formatCurrency(overpay)} crédito`
                        : `Confirmar e Dar Baixa (${formatCurrency(parsedAmountReceived)})`}
                </button>
              </div>
            </form>
          )}

          {/* ============ BULK: Barra de seleção múltipla ============ */}
          {canManage && selectedFees.length > 0 && !bulkPayOpen && (
            <div className="p-3 rounded-xl bg-[#080C14] border border-[#D4AF37]/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="bulk-select-bar">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/15 flex items-center justify-center text-[#D4AF37]">
                  <Layers className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-white">
                    {selectedFees.length} mensalidade(s) selecionada(s)
                  </div>
                  <div className="text-[10px] text-slate-400 font-mono">
                    Total a receber: <span className="text-[#D4AF37] font-bold">{formatCurrency(bulkTotalRequired)}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="btn-clear-selection"
                  onClick={() => setSelectedFeeIds([])}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 hover:text-white text-xs"
                >
                  Limpar
                </button>
                <button
                  data-testid="btn-open-bulk-pay"
                  onClick={openBulkPay}
                  className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-4 py-1.5 rounded-lg text-xs uppercase tracking-wider"
                >
                  Quitar Selecionadas
                </button>
              </div>
            </div>
          )}

          {/* ============ BULK: Formulário de pagamento em lote ============ */}
          {canManage && bulkPayOpen && selectedFees.length > 0 && (
            <form onSubmit={handleConfirmBulkPay} className="p-4 rounded-xl bg-[#080C14] border border-[#D4AF37] space-y-3" data-testid="bulk-pay-form">
              <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                <span className="font-serif font-bold text-sm text-white flex items-center gap-2">
                  <Layers className="w-4 h-4 text-[#D4AF37]" />
                  Quitar {selectedFees.length} Mensalidade(s) em Lote
                </span>
                <button
                  type="button"
                  onClick={() => { setBulkPayOpen(false); setBulkAmountReceived(""); }}
                  className="text-xs text-slate-400 hover:text-white"
                >
                  Cancelar
                </button>
              </div>

              {/* Lista de meses selecionados */}
              <div className="p-2 rounded-lg bg-[#131C2E] border border-slate-800 text-[11px] text-slate-300">
                <span className="text-slate-400 font-semibold">Meses:</span>{" "}
                {selectedFees.map((f) => f.month).join(" • ")}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-slate-400 mb-1">Data do Pagamento</label>
                  <input
                    data-testid="input-bulk-pay-date"
                    type="date"
                    required
                    value={payDate}
                    onChange={(e) => setPayDate(e.target.value)}
                    className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700 text-xs"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 mb-1">
                    Valor Recebido
                    <span className="text-[10px] text-slate-500 ml-1">(default: total)</span>
                  </label>
                  <input
                    data-testid="input-bulk-amount-received"
                    type="number"
                    step="0.01"
                    min="0"
                    value={bulkAmountReceived}
                    onChange={(e) => setBulkAmountReceived(e.target.value)}
                    className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-[#D4AF37]/40 text-xs font-mono"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 mb-1">Forma de Pagamento</label>
                  <select
                    data-testid="select-bulk-pay-method"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700 text-xs"
                  >
                    <option value="PIX">PIX (Chave da Loja)</option>
                    <option value="Boleto">Boleto Bancário</option>
                    <option value="Dinheiro">Dinheiro em Espécie</option>
                    <option value="Transferência TED">Transferência TED</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-400 mb-1">Centro de Custo</label>
                  {hasRateio ? (
                    <div
                      data-testid="bulk-pay-rateio-locked"
                      className="w-full bg-[#080C14] text-[10px] text-slate-400 p-2 rounded-lg border border-[#D4AF37]/30 italic"
                      title="Rateio Automático da Capitação está ativo — o valor será dividido entre os CCs configurados."
                    >
                      🔀 Rateio Automático ativo — dividido entre {rateio.rule_count} CC{rateio.rule_count > 1 ? "s" : ""}
                    </div>
                  ) : (
                    <select
                      value={costCenterId}
                      onChange={(e) => setCostCenterId(e.target.value)}
                      className="w-full bg-[#131C2E] text-white p-2 rounded-lg border border-slate-700 text-xs"
                    >
                      {costCenters.map(cc => (
                        <option key={cc.id} value={cc.id}>
                          {cc.code} - {cc.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* Preview calculado integralmente pelo backend */}
              {hasRateio && (() => {
                const allocs = bulkPreview?.allocations || [];
                if (allocs.length === 0) return null;
                return (
                  <div className="p-3 rounded-lg border border-[#D4AF37]/30 bg-[#080C14]" data-testid="bulk-pay-rateio-preview">
                    <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-2 flex items-center gap-1.5">
                      <Layers className="w-3 h-3" />
                      Divisão Automática (Rateio) — {selectedFees.length} mês(es) · Total {formatCurrency(bulkParsedReceived)}
                    </div>
                    <div className="space-y-1">
                      {allocs.map((a) => (
                        <div key={`${a.cost_center_id}-${a.is_multa}`} className={`flex items-center justify-between text-[11px] ${a.is_multa ? "border-t border-rose-500/30 pt-1 mt-1" : ""}`}>
                          <span className={a.is_multa ? "text-rose-300 truncate flex items-center gap-1" : "text-slate-300 truncate"}>
                            {a.is_multa && <span title="Multa por atraso — CC imutável">🔒</span>}
                            {a.cost_center_name}
                            {a.is_multa && <span className="text-[9px] italic text-rose-400/70">(multa por atraso)</span>}
                          </span>
                          <span className={`font-mono font-bold ${a.is_multa ? "text-rose-300" : "text-emerald-300"}`}>{formatCurrency(a.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Feedback */}
              <div className="space-y-2">
                <div className="p-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-xs flex items-center justify-between">
                  <span>Total devido (com desconto de pontualidade quando aplicável):</span>
                  <span className="font-mono font-bold text-sm text-white">{formatCurrency(bulkTotalRequired)}</span>
                </div>

                {bulkIsPartial && bulkParsedReceived > 0 && (
                  <div className="p-2 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-200 text-[11px] flex items-center justify-between" data-testid="bulk-partial-banner">
                    <span>⚠ Valor menor que o total. Sistema quita mês a mês em ordem; o último ficará parcial.</span>
                    <span className="font-mono font-bold">Recebendo: {formatCurrency(bulkParsedReceived)}</span>
                  </div>
                )}

                {bulkOverpay > 0 && (
                  <div className="p-2 rounded-lg border border-purple-500/40 bg-purple-500/10 text-purple-200 text-[11px] flex items-center justify-between" data-testid="bulk-overpay-banner">
                    <span>✨ Excedente de {formatCurrency(bulkOverpay)} irá para o crédito do Irmão.</span>
                    <span className="font-mono font-bold">Recebendo: {formatCurrency(bulkParsedReceived)}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-2 pt-1">
                <button
                  type="button"
                  onClick={() => { setBulkPayOpen(false); setBulkAmountReceived(""); }}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 hover:text-white text-xs"
                >
                  Voltar
                </button>
                <button
                  data-testid="btn-confirm-bulk-pay"
                  type="submit"
                  disabled={isSubmitting || bulkParsedReceived <= 0}
                  className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-4 py-1.5 rounded-lg text-xs uppercase tracking-wider disabled:opacity-40"
                >
                  {isSubmitting ? "Processando..." : `Confirmar Pagamento em Lote (${formatCurrency(bulkParsedReceived)})`}
                </button>
              </div>
            </form>
          )}

          {linked_transactions.length > 0 && (
            <div className="border border-slate-800 rounded-xl overflow-x-auto" data-testid="member-linked-transactions-section">
              <div className="min-w-[640px] flex items-center gap-2 px-3 py-2 bg-[#080C14] border-b border-slate-800">
                <Receipt className="w-4 h-4 text-sky-300" />
                <span className="text-xs uppercase font-bold tracking-wider text-sky-300">
                  Conta do Irmão — Débitos e Créditos ({linked_transactions.length})
                </span>
                <span className="text-[10px] text-slate-400 ml-auto font-mono" data-testid="member-linked-summary">
                  Débitos: <b className="text-rose-300">{formatCurrency(linked_debit_open)}</b>
                  {" • "}Créditos: <b className="text-emerald-300">{formatCurrency(linked_credit_open)}</b>
                  {" • "}Saldo: <b className={linked_net_open >= 0 ? "text-rose-300" : "text-emerald-300"}>{formatCurrency(linked_net_open)}</b>
                </span>
              </div>
              <table className="w-full min-w-[640px] text-left text-xs" data-testid="table-member-linked-transactions">
                <thead>
                  <tr className="bg-[#0B111E] border-b border-slate-800 text-slate-400 uppercase">
                    <th className="py-2 px-3">Descrição</th><th className="py-2 px-3">Categoria</th><th className="py-2 px-3">Vencimento</th>
                    <th className="py-2 px-3 text-center">Natureza</th><th className="py-2 px-3 text-right">Valor</th><th className="py-2 px-3 text-center">Status</th><th className="py-2 px-3 text-right">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {linked_transactions.map((transaction) => (
                    <tr key={transaction.id} data-testid={`member-linked-transaction-${transaction.id}`} className="hover:bg-white/5">
                      <td className="py-2 px-3 font-semibold text-white">{transaction.description}</td>
                      <td className="py-2 px-3 text-slate-400">{transaction.category}{transaction.subcategory ? ` / ${transaction.subcategory}` : ""}</td>
                      <td className="py-2 px-3 font-mono text-slate-400">{transaction.due_date ? formatBRDate(transaction.due_date) : "—"}</td>
                      <td className="py-2 px-3 text-center"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${transaction.account_nature === "debito" ? "border-rose-500/30 bg-rose-500/10 text-rose-300" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"}`}>{transaction.account_nature === "debito" ? "Débito" : "Crédito"}</span></td>
                      <td className={`py-2 px-3 text-right font-mono font-bold ${transaction.account_nature === "debito" ? "text-rose-300" : "text-emerald-300"}`}>{formatCurrency(transaction.amount)}</td>
                      <td className="py-2 px-3 text-center"><span className={`text-[10px] font-bold uppercase ${transaction.display_status === "paid" ? "text-emerald-400" : transaction.display_status === "overdue" ? "text-rose-400" : transaction.display_status === "future" ? "text-sky-300" : "text-amber-300"}`}>{transaction.display_status === "paid" ? "Pago" : transaction.display_status === "overdue" ? "Vencido" : transaction.display_status === "future" ? "A vencer" : "Em aberto"}</span></td>
                      <td className="py-2 px-3 text-right">
                        {transaction.status !== "pago" && canManage ? (
                          <button
                            data-testid={`btn-linked-transaction-paid-${transaction.id}`}
                            onClick={() => handleLinkedTransactionPaid(transaction)}
                            disabled={payingLinkedTransactionId === transaction.id}
                            className="rounded-md bg-emerald-500 px-2.5 py-1.5 text-[10px] font-bold uppercase text-slate-950 hover:bg-emerald-400 disabled:opacity-50"
                          >
                            {payingLinkedTransactionId === transaction.id ? "Baixando…" : transaction.account_nature === "debito" ? "Receber" : "Pagar"}
                          </button>
                        ) : <span className="text-slate-600">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Fees Schedule Table */}
          <div className="border border-slate-800 rounded-xl overflow-x-auto">
            <table className="w-full text-left text-[11px] sm:text-xs" data-testid="table-member-fees">
              <thead>
                <tr className="bg-[#080C14] border-b border-slate-800 text-slate-400 uppercase">
                  {canManage && (
                    <th className="py-2.5 px-3 text-center w-8">
                      <input
                        data-testid="chk-select-all-open"
                        type="checkbox"
                        onChange={toggleSelectAllOpen}
                        checked={selectableFees.length > 0 && selectableFees.every((f) => selectedFeeIds.includes(f.id))}
                        className="accent-[#D4AF37] cursor-pointer"
                        title="Selecionar todas as parcelas vencidas em aberto/parcial"
                      />
                    </th>
                  )}
                  <th className="py-2.5 px-3">Mês de Referência</th>
                  <th className="py-2.5 px-3">Vencimento</th>
                  <th className="hidden lg:table-cell py-2.5 px-3 text-right">Valor Mensalidade</th>
                  <th className="hidden lg:table-cell py-2.5 px-3 text-right">Desc. Pontualidade</th>
                  <th className="hidden lg:table-cell py-2.5 px-3 text-right">Desc. Emérito/Provecto</th>
                  <th className="py-2.5 px-3 text-right">Total a Pagar</th>
                  <th className="py-2.5 px-3 text-center">Status</th>
                  {canManage && <th className="py-2.5 px-3 text-center">Ação</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {filteredFees.map((fee) => {
                  const isPaid = fee.status === "pago";
                  const isPartial = fee.status === "parcial";
                  const isEditing = editingFeeId === fee.id;
                  const partialPaid = Number(fee.partial_paid_amount || 0);
                  const feeRemaining = Number(fee.amount_open || 0);
                  const isSelected = selectedFeeIds.includes(fee.id);
                  const isOverdue = Boolean(fee.is_overdue);
                  const daysLate = Number(fee.days_overdue || 0);

                  return (
                    <tr key={fee.id} className={`hover:bg-white/5 transition-colors ${isSelected ? "bg-[#D4AF37]/5" : ""} ${isOverdue ? "bg-rose-500/5 border-l-2 border-rose-500/60" : ""}`} data-testid={`fee-row-${fee.id}`}>
                      {canManage && (
                      <td className="py-2.5 px-3 text-center">
                        {!isPaid ? (
                          <input
                            data-testid={`chk-select-fee-${fee.id}`}
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectFee(fee.id)}
                            className="accent-[#D4AF37] cursor-pointer"
                          />
                        ) : (
                          <span className="text-slate-700">—</span>
                        )}
                      </td>
                      )}
                      
                      <td className="py-2.5 px-3 font-semibold text-white">
                        <div className="flex items-center gap-1.5">
                          {fee.month}
                          {isOverdue && (
                            <span
                              title={`${daysLate} dia(s) em atraso`}
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/50 text-[9px] font-bold uppercase tracking-wider"
                              data-testid={`overdue-badge-${fee.id}`}
                            >
                              <AlertCircle className="w-2.5 h-2.5" />
                              {daysLate}d
                            </span>
                          )}
                        </div>
                      </td>

                      <td className={`py-2.5 px-3 font-mono ${isOverdue ? "text-rose-300" : "text-slate-400"}`}>
                        {formatBRDate(fee.due_date)}
                      </td>

                      <td className="hidden lg:table-cell py-2.5 px-3 text-right font-mono text-slate-300">
                        {isEditing ? (
                          <input
                            data-testid={`input-edit-fee-amount-${fee.id}`}
                            type="number"
                            step="0.01"
                            min="0"
                            value={editAmount}
                            onChange={(e) => setEditAmount(e.target.value)}
                            className="w-24 bg-[#080C14] text-[#D4AF37] p-1.5 rounded-md border border-[#D4AF37]/40 text-right text-xs font-mono"
                            autoFocus
                          />
                        ) : (
                          formatCurrency(fee.original_amount)
                        )}
                      </td>

                      {(() => {
                        const promptDisc = Number(fee.prompt_discount_amount || 0);
                        const honorDisc = Number(fee.honor_discount_value || 0);
                        const promptShown = promptDisc;
                        const promptLost = Boolean(fee.prompt_discount_lost);
                        return (
                          <>
                            <td className="hidden lg:table-cell py-2.5 px-3 text-right font-mono">
                              {promptShown > 0 ? (
                                <span className="text-emerald-400">- {formatCurrency(promptShown)}</span>
                              ) : promptLost ? (
                                <span className="text-rose-400/70 line-through" title="Desconto perdido — venceu sem pagamento">
                                  - {formatCurrency(fee.prompt_discount_value)}
                                </span>
                              ) : (
                                <span className="text-slate-600">—</span>
                              )}
                            </td>
                            <td className="hidden lg:table-cell py-2.5 px-3 text-right font-mono">
                              {honorDisc > 0 ? (
                                <span className="text-[#D4AF37]">- {formatCurrency(honorDisc)}</span>
                              ) : (
                                <span className="text-slate-600">—</span>
                              )}
                            </td>
                          </>
                        );
                      })()}

                      <td className="py-2.5 px-3 text-right font-mono font-semibold text-[#D4AF37]">
                        {(() => {
                          return formatCurrency(fee.amount_due);
                        })()}
                      </td>

                      <td className="py-2.5 px-3 text-center">
                        {isPaid ? (
                          <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[10px] font-bold" data-testid={`status-badge-${fee.id}`}>
                            <Check className="w-3 h-3" />
                            <span>Pago ({formatCurrency(fee.paid_amount)})</span>
                          </span>
                        ) : isPartial ? (
                          <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            isOverdue
                              ? "bg-rose-500/20 text-rose-300 border border-rose-500/50"
                              : "bg-sky-500/15 text-sky-300 border border-sky-500/40"
                          }`} data-testid={`status-badge-${fee.id}`}>
                            <Coins className="w-3 h-3" />
                            <span>{isOverdue ? "Parcial em Atraso" : "Parcial"} ({formatCurrency(partialPaid)} / falta {formatCurrency(feeRemaining)})</span>
                          </span>
                        ) : isOverdue ? (
                          <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/50 text-[10px] font-bold uppercase" data-testid={`status-badge-${fee.id}`}>
                            <AlertCircle className="w-3 h-3" />
                            <span>Em Atraso · {daysLate}d</span>
                          </span>
                        ) : fee.is_future ? (
                          <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-300 border border-sky-500/30 text-[10px] font-semibold" data-testid={`status-badge-${fee.id}`}>
                            <Calendar className="w-3 h-3" />
                            <span>A Vencer</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30 text-[10px] font-semibold" data-testid={`status-badge-${fee.id}`}>
                            <Clock className="w-3 h-3" />
                            <span>Em Aberto</span>
                          </span>
                        )}
                      </td>

                      {canManage && (
                      <td className="py-2.5 px-3">
                        <div className="flex items-center justify-center gap-1.5 flex-wrap">
                          {isEditing ? (
                            <>
                              <button
                                data-testid={`btn-save-fee-${fee.id}`}
                                onClick={() => handleSaveAmount(fee)}
                                title="Salvar valor"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] font-bold hover:bg-emerald-500/25"
                              >
                                <Save className="w-3 h-3" /> Salvar
                              </button>
                              <button
                                onClick={() => setEditingFeeId(null)}
                                className="px-2 py-1 rounded-md bg-slate-800 text-slate-300 text-[10px] font-semibold hover:text-white"
                              >
                                Cancelar
                              </button>
                            </>
                          ) : (
                            <>
                              {!isPaid && (
                                <button
                                  data-testid={`btn-pay-fee-${fee.id}`}
                                  onClick={() => openPayFor(fee)}
                                  className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-2.5 py-1 rounded-md text-[10px] shadow-sm"
                                >
                                  {isPartial ? "Continuar Pagando" : "Pagar"}
                                </button>
                              )}
                              {!isPaid && (
                                <button
                                  data-testid={`btn-edit-fee-${fee.id}`}
                                  onClick={() => {
                                    setEditingFeeId(fee.id);
                                    setEditAmount(String(fee.original_amount));
                                  }}
                                  title="Editar valor (se sua Loja pratica valores diferentes)"
                                  className="p-1 rounded-md border border-slate-700 text-slate-300 hover:text-white hover:border-[#D4AF37] transition-colors"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button
                                data-testid={`btn-delete-fee-${fee.id}`}
                                onClick={() => handleReverseOrDelete(fee)}
                                title={isPaid ? "Estornar pagamento (lançamento errado)" : "Remover lançamento"}
                                className="p-1 rounded-md border border-rose-500/30 text-rose-400 hover:bg-rose-500/10 transition-colors"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                              {isPaid && (
                                <span className="text-[9px] text-slate-500 font-mono ml-1">
                                  {fee.payment_method} • {fee.paid_at ? formatBRDate(fee.paid_at) : "Quitado"}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      )}

                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ============ Cobranças de Eventos ============ */}
          {filteredEventCharges.length > 0 && (
            <div className="border border-purple-500/30 rounded-xl overflow-hidden">
              <div className="px-3 py-2 bg-purple-500/10 border-b border-purple-500/30 flex items-center gap-2">
                <span className="text-xs uppercase font-bold tracking-wider text-purple-300">
                  Eventos & Cobranças Avulsas ({filteredEventCharges.length})
                </span>
                <span className="text-[10px] text-slate-400 ml-auto font-mono">
                  Recebido: <span className="text-emerald-400 font-bold">{formatCurrency(event_total_paid)}</span> • A Receber: <span className="text-[#D4AF37] font-bold">{formatCurrency(event_total_open)}</span>
                </span>
              </div>
              <table className="w-full text-left text-xs" data-testid="table-event-charges-in-stmt">
                <thead>
                  <tr className="bg-[#080C14] border-b border-slate-800 text-slate-400 uppercase">
                    <th className="py-2 px-3">Evento</th>
                    <th className="py-2 px-3">Vencimento</th>
                    <th className="py-2 px-3 text-center">Convidados</th>
                    <th className="py-2 px-3 text-center">Menores</th>
                    <th className="py-2 px-3 text-right">Total</th>
                    <th className="py-2 px-3 text-center">Status</th>
                    <th className="py-2 px-3 text-center">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {filteredEventCharges.map((c) => {
                    const isPaid = c.status === "pago";
                    const isPartial = c.status === "parcial";
                    const isPaying = payingEventChargeId === c.id;
                    return (
                      <React.Fragment key={c.id}>
                        <tr className="hover:bg-white/5">
                          <td className="py-2 px-3 font-semibold text-white">{c.event_name}</td>
                          <td className="py-2 px-3 text-slate-400 font-mono">{c.due_date ? formatBRDate(c.due_date) : "—"}</td>
                          <td className="py-2 px-3 text-center">{c.adults || 0}</td>
                          <td className="py-2 px-3 text-center">{c.minors || 0}</td>
                          <td className="py-2 px-3 text-right font-mono font-bold text-[#D4AF37]">{formatCurrency(c.total_amount)}</td>
                          <td className="py-2 px-3 text-center">
                            {isPaid ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[10px] font-bold">
                                <Check className="w-3 h-3" /> Pago
                              </span>
                            ) : isPartial ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/40 text-[10px] font-bold">
                                <Coins className="w-3 h-3" /> Parcial (falta {formatCurrency(c.remaining_amount)})
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30 text-[10px] font-semibold">
                                <Clock className="w-3 h-3" /> Em Aberto
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center">
                            {!isPaid && (
                              <button
                                data-testid={`btn-pay-event-charge-${c.id}`}
                                onClick={() => openPayEventCharge(c)}
                                className="bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-2.5 py-1 rounded-md text-[10px]"
                              >
                                {isPartial ? "Continuar" : "Pagar"}
                              </button>
                            )}
                          </td>
                        </tr>
                        {isPaying && (
                          <tr>
                            <td colSpan={7} className="p-3 bg-[#080C14] border-t border-purple-500/30">
                              <form onSubmit={(e) => submitPayEventCharge(e, c)} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
                                <div>
                                  <label className="text-[10px] text-slate-400 block mb-0.5">Data</label>
                                  <input type="date" value={evPayForm.payment_date} onChange={(e) => setEvPayForm({ ...evPayForm, payment_date: e.target.value })} className="w-full bg-[#131C2E] text-white p-1.5 rounded border border-slate-700 text-xs" required />
                                </div>
                                <div>
                                  <label className="text-[10px] text-slate-400 block mb-0.5">Valor Recebido</label>
                                  <input data-testid={`input-pay-ev-amount-${c.id}`} type="number" step="0.01" min="0" value={evPayForm.amount_received} onChange={(e) => setEvPayForm({ ...evPayForm, amount_received: e.target.value })} className="w-full bg-[#131C2E] text-white p-1.5 rounded border border-[#D4AF37]/40 text-xs font-mono" />
                                </div>
                                <div>
                                  <label className="text-[10px] text-slate-400 block mb-0.5">Forma</label>
                                  <select value={evPayForm.payment_method} onChange={(e) => setEvPayForm({ ...evPayForm, payment_method: e.target.value })} className="w-full bg-[#131C2E] text-white p-1.5 rounded border border-slate-700 text-xs">
                                    <option value="PIX">PIX</option>
                                    <option value="Boleto">Boleto</option>
                                    <option value="Dinheiro">Dinheiro</option>
                                    <option value="Transferência TED">TED</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="text-[10px] text-slate-400 block mb-0.5">Centro de Custo</label>
                                  <select value={evPayForm.cost_center_id} onChange={(e) => setEvPayForm({ ...evPayForm, cost_center_id: e.target.value })} className="w-full bg-[#131C2E] text-white p-1.5 rounded border border-slate-700 text-xs">
                                    {(costCenters || []).map((cc) => (
                                      <option key={cc.id} value={cc.id}>{cc.code} - {cc.name}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="flex gap-1">
                                  <button type="button" onClick={() => setPayingEventChargeId(null)} className="flex-1 px-2 py-1.5 rounded bg-slate-800 text-slate-300 hover:text-white text-xs">Cancelar</button>
                                  <button data-testid={`btn-confirm-pay-ev-${c.id}`} type="submit" disabled={isSubmitting} className="flex-1 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-2 py-1.5 rounded text-xs disabled:opacity-40">Confirmar</button>
                                </div>
                              </form>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

        </div>

      </div>

      {/* ============ PRINT / PDF ROOT (visible apenas na impressão) ============ */}
      <div id="stmt-print-root" style={{ position: "absolute", left: "-99999px", top: 0, width: "210mm", background: "#fff", padding: "0" }}>
        <style>{`
          #stmt-print-root { font-family: Georgia, 'Times New Roman', serif; color: #111; padding: 0; }
          #stmt-print-root h1 { font-size: 22px; margin: 0 0 4px 0; letter-spacing: 0.5px; }
          #stmt-print-root h2 { font-size: 14px; margin: 18px 0 6px 0; padding-bottom: 4px; border-bottom: 1.5px solid #8B6F1F; color: #8B6F1F; text-transform: uppercase; letter-spacing: 1px; }
          #stmt-print-root .stmt-meta { font-size: 11px; color: #444; }
          #stmt-print-root table.stmt-table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 4px; }
          #stmt-print-root table.stmt-table th { background: #f3ecd3; color: #5b4713; text-align: left; padding: 6px 8px; border: 1px solid #d9c98a; font-size: 10px; letter-spacing: 0.4px; text-transform: uppercase; }
          #stmt-print-root table.stmt-table td { padding: 5px 8px; border: 1px solid #e2ddc8; vertical-align: top; }
          #stmt-print-root table.stmt-table td.num { text-align: right; font-family: 'Courier New', monospace; }
          #stmt-print-root table.stmt-table tr.paid { background: #eaf7ed; }
          #stmt-print-root table.stmt-table tr.partial { background: #eef4fb; }
          #stmt-print-root table.stmt-table tr.future { background: #fbf7ea; }
          #stmt-print-root table.stmt-table tr.overdue { background: #fdecec; }
          #stmt-print-root table.stmt-table tr.overdue td { border-color: #f3c1c1; }
          #stmt-print-root .badge-atraso { display: inline-block; padding: 1px 6px; border-radius: 8px; background: #dc2626; color: #fff; font-size: 9px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px; margin-left: 4px; }
          #stmt-print-root .disc-lost { color: #b91c1c; text-decoration: line-through; opacity: 0.7; }
          #stmt-print-root .stmt-summary { display: flex; gap: 12px; margin-top: 12px; flex-wrap: wrap; }
          #stmt-print-root .stmt-summary .card { flex: 1; min-width: 120px; border: 1px solid #d9c98a; padding: 8px 10px; border-radius: 4px; }
          #stmt-print-root .stmt-summary .card b { display: block; font-size: 10px; color: #6c5b1f; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
          #stmt-print-root .stmt-summary .card span { font-family: 'Courier New', monospace; font-size: 14px; font-weight: bold; }
          #stmt-print-root .stmt-footer { margin-top: 40px; display: flex; justify-content: space-between; font-size: 10px; color: #666; }
          #stmt-print-root .stmt-sign { border-top: 1px solid #333; text-align: center; width: 220px; padding-top: 4px; }
        `}</style>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, borderBottom: "2px solid #8B6F1F", paddingBottom: 10 }}>
          {treasurySettings?.logo_b64 || member?.loja_logo ? (
            <img src={treasurySettings?.logo_b64 || member?.loja_logo} alt="Brasão" style={{ width: 64, height: 64, objectFit: "contain" }} />
          ) : null}
          <div style={{ flex: 1 }}>
            <h1>Extrato Financeiro Individual</h1>
            <div className="stmt-meta">
              {treasurySettings?.loja_name || member?.loja_name || "Loja Maçônica"}
              {(treasurySettings?.loja_number || member?.loja_number) && ` • Nº ${treasurySettings?.loja_number || member?.loja_number}`}
            </div>
            <div className="stmt-meta" style={{ marginTop: 4 }}>
              <strong>Período:</strong> {effStart ? formatBRDate(effStart) : "—"} até {effEnd ? formatBRDate(effEnd) : "—"} • <strong>Emitido em:</strong> {formatBRDate(todayISO)}
            </div>
          </div>
        </div>

        {/* Member data */}
        <div style={{ marginTop: 10, fontSize: 12 }}>
          <div><strong>Irmão:</strong> {member.name}</div>
          <div className="stmt-meta">
            CIM: {member.cim} • Grau: {member.degree} • Cargo: {member.office}
          </div>
        </div>

        {/* Passado */}
        <h2>Lançamentos do Período ({pastFees.length})</h2>
        {pastFees.length === 0 ? (
          <div style={{ fontSize: 11, color: "#888", fontStyle: "italic", padding: "8px 0" }}>Nenhum lançamento no período.</div>
        ) : (
          <table className="stmt-table">
            <thead>
              <tr>
                <th>Referência</th>
                <th>Vencimento</th>
                <th style={{ textAlign: "right" }}>Valor Mensalidade</th>
                <th style={{ textAlign: "right" }}>Desc. Pontualidade</th>
                <th style={{ textAlign: "right" }}>Desc. Emérito/Provecto</th>
                <th>Status</th>
                <th>Pagamento</th>
              </tr>
            </thead>
            <tbody>
              {pastFees.map((f) => {
                const st = f.status;
                const isOverdue = Boolean(f.is_overdue);
                const daysLate = Number(f.days_overdue || 0);
                const rowClass = st === "pago" ? "paid" : isOverdue ? "overdue" : st === "parcial" ? "partial" : "";
                const statusLabel = st === "pago"
                  ? `Pago (${formatCurrency(f.paid_amount || 0)})`
                  : st === "parcial"
                    ? (isOverdue
                        ? `Parcial em Atraso (${formatCurrency(f.partial_paid_amount || 0)} / falta ${formatCurrency(f.remaining_amount || 0)})`
                        : `Parcial (${formatCurrency(f.partial_paid_amount || 0)} / falta ${formatCurrency(f.remaining_amount || 0)})`)
                    : (isOverdue ? "Em Atraso" : "Em Aberto");
                const payInfo = st === "pago"
                  ? `${f.payment_method || "-"} • ${f.paid_at ? formatBRDate(f.paid_at) : "-"}${f.proof_code ? " • " + f.proof_code : ""}`
                  : "—";
                const promptDisc = Number(f.prompt_discount_amount || 0);
                const honorDisc = Number(f.honor_discount_value || 0);
                const promptShown = promptDisc;
                const promptLost = Boolean(f.prompt_discount_lost);
                return (
                  <tr key={f.id} className={rowClass}>
                    <td>
                      {f.month}
                      {isOverdue && <span className="badge-atraso">⚠ {daysLate}d</span>}
                    </td>
                    <td style={{ color: isOverdue ? "#b91c1c" : undefined, fontWeight: isOverdue ? "bold" : undefined }}>
                      {formatBRDate(f.due_date)}
                    </td>
                    <td className="num">{formatCurrency(f.original_amount)}</td>
                    <td className="num" style={{ color: promptShown > 0 ? "#059669" : "#999" }}>
                      {promptShown > 0
                        ? `- ${formatCurrency(promptShown)}`
                        : promptLost
                          ? <span className="disc-lost">- {formatCurrency(f.prompt_discount_value)}</span>
                          : "—"}
                    </td>
                    <td className="num" style={{ color: honorDisc > 0 ? "#B8860B" : "#999" }}>
                      {honorDisc > 0 ? `- ${formatCurrency(honorDisc)}` : "—"}
                    </td>
                    <td style={{ color: isOverdue && st !== "pago" ? "#b91c1c" : undefined, fontWeight: isOverdue ? "bold" : undefined }}>
                      {statusLabel}
                    </td>
                    <td style={{ fontSize: 10 }}>{payInfo}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Futuro */}
        {futureFees.length > 0 && (
          <>
            <h2>Lançamentos Futuros ({futureFees.length})</h2>
            <table className="stmt-table">
              <thead>
                <tr>
                  <th>Referência</th>
                  <th>Vencimento</th>
                  <th style={{ textAlign: "right" }}>Valor Mensalidade</th>
                  <th style={{ textAlign: "right" }}>Desc. Pontualidade</th>
                  <th style={{ textAlign: "right" }}>Desc. Emérito/Provecto</th>
                </tr>
              </thead>
              <tbody>
                {futureFees.map((f) => {
                  const promptDisc = Number(f.prompt_discount_amount || 0);
                  const honorDisc = Number(f.honor_discount_value || 0);
                  return (
                    <tr key={f.id} className="future">
                      <td>{f.month}</td>
                      <td>{formatBRDate(f.due_date)}</td>
                      <td className="num">{formatCurrency(f.original_amount)}</td>
                      <td className="num" style={{ color: promptDisc > 0 ? "#059669" : "#999" }}>
                        {promptDisc > 0 ? `- ${formatCurrency(promptDisc)}` : "—"}
                      </td>
                      <td className="num" style={{ color: honorDisc > 0 ? "#B8860B" : "#999" }}>
                        {honorDisc > 0 ? `- ${formatCurrency(honorDisc)}` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}

        {linked_transactions.length > 0 && (
          <>
            <h2 style={{ borderBottomColor: "#0369a1", color: "#0369a1" }}>Débitos e Créditos Vinculados ({linked_transactions.length})</h2>
            <table className="stmt-table">
              <thead><tr><th>Descrição</th><th>Categoria</th><th>Vencimento</th><th>Natureza</th><th style={{ textAlign: "right" }}>Valor</th><th>Status</th></tr></thead>
              <tbody>
                {linked_transactions.map((transaction) => (
                  <tr key={transaction.id} className={transaction.display_status === "paid" ? "paid" : transaction.display_status === "future" ? "future" : transaction.display_status === "overdue" ? "overdue" : ""}>
                    <td>{transaction.description}</td>
                    <td>{transaction.category}{transaction.subcategory ? ` / ${transaction.subcategory}` : ""}</td>
                    <td>{transaction.due_date ? formatBRDate(transaction.due_date) : "—"}</td>
                    <td>{transaction.account_nature === "debito" ? "Débito" : "Crédito"}</td>
                    <td className="num">{formatCurrency(transaction.amount)}</td>
                    <td>{transaction.display_status === "paid" ? "Pago" : transaction.display_status === "overdue" ? "Vencido" : transaction.display_status === "future" ? "A vencer" : "Em aberto"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Eventos & Cobranças Avulsas */}
        {filteredEventCharges.length > 0 && (
          <>
            <h2 style={{ borderBottomColor: "#6d28d9", color: "#6d28d9" }}>Eventos & Cobranças Avulsas ({filteredEventCharges.length})</h2>
            <table className="stmt-table">
              <thead>
                <tr>
                  <th>Evento</th>
                  <th>Vencimento</th>
                  <th style={{ textAlign: "center" }}>Convidados</th>
                  <th style={{ textAlign: "center" }}>Menores</th>
                  <th style={{ textAlign: "right" }}>Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredEventCharges.map((c) => {
                  const st = c.status;
                  const rowClass = st === "pago" ? "paid" : st === "parcial" ? "partial" : "";
                  const statusLabel = st === "pago"
                    ? `Pago (${formatCurrency(c.paid_amount)})`
                    : st === "parcial"
                      ? `Parcial (${formatCurrency(c.partial_paid_amount)} / falta ${formatCurrency(c.remaining_amount)})`
                      : "Em Aberto";
                  return (
                    <tr key={c.id} className={rowClass}>
                      <td>{c.event_name}</td>
                      <td>{c.due_date ? formatBRDate(c.due_date) : "—"}</td>
                      <td style={{ textAlign: "center" }}>{c.adults || 0}</td>
                      <td style={{ textAlign: "center" }}>{c.minors || 0}</td>
                      <td className="num">{formatCurrency(c.total_amount)}</td>
                      <td style={{ fontSize: 10 }}>{statusLabel}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}

        {/* Totals */}
        <div className="stmt-summary">
          <div className="card"><b>Mensalidades — Quitado</b><span style={{ color: "#047857" }}>{formatCurrency(total_paid)}</span></div>
          <div className="card"><b>Mensalidades — A quitar</b><span style={{ color: "#8B6F1F" }}>{formatCurrency(total_open)}</span></div>
          {total_overdue > 0 && (
            <div className="card" style={{ borderColor: "#dc2626", background: "#fdecec" }}>
              <b style={{ color: "#b91c1c" }}>⚠ Em Atraso ({overdue_count})</b>
              <span style={{ color: "#b91c1c" }}>{formatCurrency(total_overdue)}</span>
            </div>
          )}
          {filteredEventCharges.length > 0 && (
            <>
              <div className="card"><b>Eventos — Quitado</b><span style={{ color: "#047857" }}>{formatCurrency(event_total_paid)}</span></div>
              <div className="card"><b>Eventos — A quitar</b><span style={{ color: "#6d28d9" }}>{formatCurrency(event_total_open)}</span></div>
            </>
          )}
          {linked_transactions.length > 0 && (
            <>
              <div className="card"><b>Débitos Vinculados</b><span style={{ color: "#b91c1c" }}>{formatCurrency(linked_debit_open)}</span></div>
              <div className="card"><b>Créditos Vinculados</b><span style={{ color: "#047857" }}>{formatCurrency(linked_credit_open)}</span></div>
            </>
          )}
          {credit_balance > 0 && (
            <div className="card"><b>Crédito do Irmão</b><span style={{ color: "#6d28d9" }}>{formatCurrency(credit_balance)}</span></div>
          )}
        </div>

        {/* Footer */}
        <div className="stmt-footer">
          <div>
            Documento emitido pela Tesouraria • Lojas Integradas
          </div>
          <div className="stmt-sign">
            Tesoureiro
          </div>
        </div>
      </div>
    </div>
  );
};
